import React, { useState, useRef, useEffect } from 'react';
import { graphBuilderApi } from '../../services/api';
import './NLGeneratorPanel.css';

const EXAMPLE_CHIPS = [
  {
    label: '2-step: query a graph then summarise',
    prompt:
      'Create a 2-step process: first query a knowledge graph with the user question, then pass the result to an LLM that summarises it into a concise executive summary.',
  },
  {
    label: '3-step: extract, analyse, report',
    prompt:
      'Build a 3-node process: (1) an LLM node that extracts key entities from the user input, (2) an engine pipeline node that queries a knowledge graph using those entities, and (3) a final LLM node that generates a structured report from the graph results.',
  },
  {
    label: 'Parallel: 2 graphs + AND gate merge',
    prompt:
      'Design a parallel process: two engine pipeline nodes querying different knowledge graphs in parallel, connected through an AND gate, followed by a single LLM node that merges and synthesises both results.',
  },
];

export default function NLGeneratorPanel({
  availableGraphs = [],
  availableRouters = [],
  onApply,
  hasExistingNodes,
  onClose,
}) {
  const [prompt, setPrompt] = useState('');
  const [history, setHistory] = useState([]);
  const [generatedJSON, setGeneratedJSON] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showJSON, setShowJSON] = useState(false);
  const [judgeApplied, setJudgeApplied] = useState(false);

  const textareaRef = useRef(null);

  useEffect(() => {
    if (!loading && !generatedJSON && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [loading, generatedJSON]);

  const graphList = [
    ...availableGraphs.map((g) => ({
      graph_id: g.graphId,
      graph_name: g.graphName,
      graph_type: g.graphType || 'Standard',
    })),
    ...availableRouters.map((r) => ({
      graph_id: r.graphId,
      graph_name: r.graphName,
      graph_type: 'Router',
    })),
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    setGeneratedJSON(null);
    setValidationWarnings([]);
    setJudgeApplied(false);

    try {
      const data = await graphBuilderApi.generateProcessFromNL(
        prompt.trim(),
        history,
        graphList,
        'gpt-4o-mini'
      );

      if (!data.success) {
        setError(data.error || 'Generation failed');
        return;
      }

      setGeneratedJSON(data.definition);
      setValidationWarnings(data.validation_warnings || []);
      setJudgeApplied(!!data.judge_applied);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'Request failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!generatedJSON) return;

    if (hasExistingNodes) {
      const ok = window.confirm(
        'This will replace your current process map. Continue?'
      );
      if (!ok) return;
    }

    onApply(generatedJSON);
  };

  const handleRefine = () => {
    setHistory((prev) => [
      ...prev,
      { role: 'user', content: prompt },
      { role: 'assistant', content: JSON.stringify(generatedJSON) },
    ]);
    setGeneratedJSON(null);
    setValidationWarnings([]);
    setPrompt('');
    setError(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleChipClick = (chipPrompt) => {
    setPrompt(chipPrompt);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const nodeCount = generatedJSON?.nodes?.length ?? 0;
  const synapseCount = generatedJSON?.synapses?.length ?? 0;
  const gateCount = generatedJSON?.gates?.length ?? 0;

  return (
    <div className="nl-generator-panel">
      <div className="nl-panel-header">
        <h3>AI Process Generator</h3>
        <button className="nl-close-btn" onClick={onClose} title="Close">
          &times;
        </button>
      </div>

      {/* Conversation history */}
      {history.length > 0 && (
        <div className="nl-history">
          {history
            .filter((m) => m.role === 'user')
            .map((m, i) => (
              <div key={i} className="nl-history-item">
                <span className="nl-history-label">Prompt {i + 1}:</span>
                <span className="nl-history-text">
                  {m.content.length > 80
                    ? m.content.substring(0, 80) + '...'
                    : m.content}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="nl-loading">
          <div className="nl-spinner"></div>
          <span>Generating your process map... (typically 10-30 seconds)</span>
        </div>
      )}

      {/* Prompt input */}
      {!loading && !generatedJSON && (
        <div className="nl-prompt-section">
          <textarea
            ref={textareaRef}
            className="nl-textarea"
            rows={5}
            placeholder="Describe your workflow in plain English...&#10;&#10;e.g. &quot;Create a process that takes a user question, queries two knowledge graphs in parallel, and merges the results into a final report.&quot;"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <div className="nl-chips">
            {EXAMPLE_CHIPS.map((chip, i) => (
              <button
                key={i}
                className="nl-chip"
                onClick={() => handleChipClick(chip.prompt)}
              >
                {chip.label}
              </button>
            ))}
          </div>

          <div className="nl-actions">
            <button
              className="nl-generate-btn"
              disabled={!prompt.trim()}
              onClick={handleGenerate}
            >
              Generate
            </button>
            <span className="nl-hint">Ctrl+Enter to generate</span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="nl-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Preview */}
      {generatedJSON && !loading && (
        <div className="nl-preview">
          <div className="nl-preview-summary">
            Generated <strong>{nodeCount}</strong> node{nodeCount !== 1 ? 's' : ''}
            , <strong>{synapseCount}</strong> connection{synapseCount !== 1 ? 's' : ''}
            {gateCount > 0 && (
              <>
                , <strong>{gateCount}</strong> gate{gateCount !== 1 ? 's' : ''}
              </>
            )}
            {judgeApplied && (
              <span className="nl-judge-badge">Auto-corrected by validator</span>
            )}
          </div>

          {validationWarnings.length > 0 && (
            <div className="nl-warnings">
              <div className="nl-warnings-title">Validation warnings</div>
              <ul>
                {validationWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <button
            className="nl-toggle-json"
            onClick={() => setShowJSON(!showJSON)}
          >
            {showJSON ? 'Hide' : 'Show'} JSON preview
          </button>

          {showJSON && (
            <pre className="nl-json-preview">
              {JSON.stringify(generatedJSON, null, 2)}
            </pre>
          )}

          <div className="nl-preview-actions">
            <button className="nl-apply-btn" onClick={handleApply}>
              Apply to Canvas
            </button>
            <button className="nl-refine-btn" onClick={handleRefine}>
              Refine
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
